import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-20 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last updated: January 12, 2026</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed mb-6">
              Please read these Terms of Service ("Terms", "Terms of Service") carefully
              before using the dakkio.io website and service (the "Service") operated by
              MalexSoftware / RemoteSystems.ro ("us", "we", or "our").
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              By accessing or using our Service, you agree to be bound by these Terms. If you
              disagree with any part of the terms, you may not access the Service.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              2. Description of Service
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              dakkio.io provides a time-series data monitoring platform with natural language
              interface, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Time-series data storage and querying</li>
              <li>Natural language alert configuration</li>
              <li>Webhook notification system</li>
              <li>Embeddable visualization widgets</li>
              <li>API access for data ingestion and retrieval</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">3. Accounts</h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.1 Account Creation
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You must provide accurate, complete, and current information during the
              registration process. You are responsible for safeguarding your account
              password and for any activities or actions under your account.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.2 Account Security
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You must notify us immediately upon becoming aware of any breach of security
              or unauthorized use of your account. We will not be liable for any loss or
              damage arising from your failure to comply with this security obligation.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.3 Account Termination
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We may terminate or suspend your account and bar access to the Service
              immediately, without prior notice or liability, under our sole discretion,
              for any reason whatsoever, including without limitation if you breach the Terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              4. Acceptable Use
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              4.1 Prohibited Activities
            </h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>
                Transmit any harmful, threatening, abusive, or defamatory content
              </li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>
                Engage in any activity that could damage, disable, or impair the Service
              </li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              4.2 API Usage
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You must comply with rate limits and usage quotas associated with your plan.
              Excessive or abusive API usage may result in temporary or permanent suspension
              of access.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              5. Subscription and Billing
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              5.1 Pricing
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Pricing for the Service is described on our pricing page. We reserve the right
              to change pricing with 30 days notice to existing customers. Price changes
              will not affect your current billing cycle.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              5.2 Billing Cycle
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Subscription fees are billed in advance on a monthly or annual basis. Your
              subscription will automatically renew unless you cancel before the renewal date.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              5.3 Cancellation
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You may cancel your subscription at any time. Cancellation takes effect at the
              end of your current billing period. No refunds are provided for partial months
              or unused data quotas.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              5.4 Payment Information
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You agree to provide current, complete, and accurate purchase and account
              information. You must promptly update account and payment information to keep
              it current and complete.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              6. Intellectual Property
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.1 Service Ownership
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              The Service and its original content, features, and functionality are owned
              by MalexSoftware / RemoteSystems.ro and are protected by international
              copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.2 Your Data
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You retain all rights to the data you store in the Service. By using the Service,
              you grant us permission to store, process, and display your data solely for the
              purpose of providing the Service to you.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              6.3 Feedback
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Any feedback, comments, or suggestions you provide about the Service may be
              used by us without any obligation to compensate you.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              7. Data Privacy and Security
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Your use of the Service is also governed by our Privacy Policy. Please review
              our Privacy Policy, which explains how we collect, use, and protect your
              personal information.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              8. Service Availability and Modifications
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              8.1 Availability
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We strive to maintain high availability but do not guarantee uninterrupted
              access. We may experience scheduled or unscheduled downtime for maintenance,
              upgrades, or technical issues.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              8.2 Service Modifications
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              We reserve the right to modify or discontinue the Service (or any part thereof)
              at any time with or without notice. We shall not be liable to you or any third
              party for any modification, suspension, or discontinuance of the Service.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              9. Limitation of Liability
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              To the maximum extent permitted by applicable law, in no event shall
              MalexSoftware / RemoteSystems.ro, its directors, employees, partners, agents,
              suppliers, or affiliates be liable for any indirect, incidental, special,
              consequential, or punitive damages, including without limitation, loss of
              profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>Your access to or use of or inability to access or use the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Unauthorized access, use, or alteration of your data</li>
              <li>Any other matter relating to the Service</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              10. Disclaimer of Warranties
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is
              provided without warranties of any kind, whether express or implied, including,
              but not limited to, implied warranties of merchantability, fitness for a
              particular purpose, non-infringement, or course of performance.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              11. Indemnification
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              You agree to defend, indemnify, and hold harmless MalexSoftware /
              RemoteSystems.ro and its licensee and licensors, and their employees,
              contractors, agents, officers, and directors, from and against any and all
              claims, damages, obligations, losses, liabilities, costs or debt, and expenses
              arising from: (i) your use of and access to the Service; (ii) your violation
              of any term of these Terms; (iii) your violation of any third party right; or
              (iv) any claim that your data caused damage to a third party.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              12. Governing Law
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              These Terms shall be governed by and construed in accordance with the laws of
              Romania, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              13. Dispute Resolution
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Any dispute arising out of or relating to these Terms or the Service shall be
              resolved through binding arbitration in accordance with the rules of the
              Romanian Arbitration Court, except that either party may seek injunctive relief
              in any court of competent jurisdiction.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              14. Changes to Terms
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We reserve the right to modify or replace these Terms at any time. If a revision
              is material, we will provide at least 30 days notice prior to any new terms
              taking effect. What constitutes a material change will be determined at our
              sole discretion. By continuing to access or use our Service after any revisions
              become effective, you agree to be bound by the revised terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              15. Severability
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              If any provision of these Terms is held to be unenforceable or invalid, such
              provision will be changed and interpreted to accomplish the objectives of such
              provision to the greatest extent possible under applicable law, and the remaining
              provisions will continue in full force and effect.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              16. Contact Information
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              If you have any questions about these Terms, please contact us:
            </p>
            <ul className="list-none space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Email:</strong>{' '}
                <a
                  href="mailto:legal@dakkio.io"
                  className="text-primary-600 hover:text-primary-700"
                >
                  legal@dakkio.io
                </a>
              </li>
              <li>
                <strong>Website:</strong>{' '}
                <a
                  href="https://remotesystems.ro"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  remotesystems.ro
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
